import React, { useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faTrash as solidStar } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { Document, Page, Text, View, Image, StyleSheet, pdf, Font } from '@react-pdf/renderer';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import '../styles/SearchResult.css';
import QRCode from 'qrcode';
import { translateText } from '../components/translateText';
import { APIBaseLink } from '../components/APIBaseLink';

Font.register({
    family: 'Lato',
    fonts: [
      {
        src: 'https://fonts.gstatic.com/s/lato/v11/iX_QxBBZLhNj5JHlTzHQzg.ttf',
        fontWeight: 700, // Lato Bold
      },
      {
        src: 'https://fonts.gstatic.com/s/lato/v11/h7rISIcQapZBpei-sXwIwg.ttf',
        fontWeight: 400, // Lato Regular
      },
    ],
});
  
const styles = StyleSheet.create({
    page: {
      padding: 15,
      fontSize: 11,
      fontFamily: 'Lato',
      flexDirection: 'column',
    },
    resultContainer: {
      marginBottom: 10,
      padding: 15,
      borderBottomWidth: 1,
      borderBottomColor: '#ccc',
      borderBottomStyle: 'solid',
    },
    titleContainer: {
      backgroundColor: '#210062',
      padding: 5,
      marginBottom: 5,
    },
    title: {
      fontSize: 16,
      color: 'white',
      textAlign: 'center',
    },
    contentRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    content: {
      flex: 1,
      marginRight: 35,
    },
    section: {
      marginBottom: 2,
    },
    text: {
      fontWeight: 400,
      marginBottom: 10,
      marginLeft: 10,
    },
    label: {
      fontWeight: 'bold',
    },
    qrCodeContainer: {
      width: 100,
      height: 100,  // Make the container square
      alignItems: 'center',
      justifyContent: 'center',
      // Remove padding if it causes scaling issues
    },
    qrCodeImage: {
      width: 90,  // Slightly smaller than the container to allow for centering
      height: 90,
      // Remove additional margins that might affect sizing
    },
    
});

const getSelectedLanguage = () => {
    const select = document.querySelector('.goog-te-combo');
    return select ? select.value : 'en';
  }

const Favorites = ({ result, index }) => {
    const { user, updateUserFavorites } = useContext(AuthContext); // Access user and updateUserFavorites function
    const [resources, setResources] = useState([]); // Store resources (saved items)
    const [page, setPage] = useState(1); // Store current page
    const [totalPages, setTotalPages] = useState(1); // Store total number of pages
    const [loading, setLoading] = useState(true); // Loading state to show spinner
    const navigate = useNavigate();

    const API_BASE_LINK = APIBaseLink();

    useEffect(() => {
        if (user?.id) {
            setLoading(true); // Set loading state before making the API call

            fetch(`${API_BASE_LINK}/user/getSaved?userId=${user.id}&page=${page}&limit=10`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data?.resources) {
                        setTotalPages(Math.ceil(data.total / 10)); // Calculate total pages
                        setResources(data.resources); // Set the resources for current page
                    } else {
                        setResources([]); // If no resources, set as empty array
                    }
                    setLoading(false); // Set loading to false when data is fetched
                })
                .catch((error) => {
                    console.error('Error fetching saved resources:', error);
                    setResources([]); // If error, clear the resources
                    setLoading(false); // Set loading to false on error
                });
        }
    }, [page, user?.id]); // Fetch data when page or user.id changes

    // Helper function to check if a field is valid (not "N/A", "NA", or empty string)
    const isValidField = (field) => {
        return typeof field === 'string' && field.toLowerCase() !== "n/a" && field.toLowerCase() !== "na" && field.trim() !== '';
    };

    // Function to handle unfavoriting
    const handleToggleFavorite = (resourceId) => {
        updateUserFavorites(resourceId, false); // Update state in AuthContext
        const updatedResources = resources.filter((resource) => resource._id !== resourceId); // Remove the unfavorited resource
        setResources(updatedResources);

        // Call API to update backend
        // fetch(`https://mycommunityfl.com/user/saveResourceToggle`, {
        fetch(`${API_BASE_LINK}/user/saveResourceToggle`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: user.id, resourceId }),
        }).catch((error) => console.error('Error unfavoriting resource:', error));
    };

    // Function to find URLs in text and make them clickable links
    const makeTextClickable = (text) => {
        if (!text) return text;

        // Enhanced regex to handle URLs in parentheses or with punctuation around them
        const urlPattern = /(?:\()?(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.(com|org|net|gov|edu|info))(?:\))?/gi;

        return text.replace(urlPattern, (match) => {
        // Remove surrounding parentheses if they exist
        const cleanedMatch = match.replace(/[()]/g, "");
        const href = cleanedMatch.startsWith('http') ? cleanedMatch : `http://${cleanedMatch}`;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${cleanedMatch}</a>`;
        });
    };

    const createMarkup = (text) => {
        return { __html: makeTextClickable(text) };
    };

    // Function to generate Google Maps link for the address
    const getGoogleMapsLink = (address) => {
        const encodedAddress = encodeURIComponent(address); // Encode the address to be used in the URL
        return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    };

    const generateCombinedPDF = async () => {
        if (resources.length === 0) {
          console.error('No results selected for PDF generation.');
          return;
        }
    
        const language = getSelectedLanguage();
        
        const apiKey=process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY;
    
        const labelsToTranslate = {
          services: "Services",
          eligibility: "Eligibility Requirements",
          phone: "Phone",
          email: "Email",
          address: "Address",
          website: "Website",
          howToApply: "How to Apply"
        };
        
        const translatedLabels = {};
        for (const key in labelsToTranslate) {
          translatedLabels[key] = await translateText(labelsToTranslate[key], language, apiKey);
        }
    
        const translatedResults = await Promise.all(
          resources.map(async (resource) => {
            return {
              ...resource,
              Services: await translateText(resource.Services, language, apiKey),
              Eligibility_Requirement: await translateText(resource.Eligibility_Requirement, language, apiKey),
              How_To_Apply: await translateText(resource.How_To_Apply, language, apiKey),
            };
          })
        );
    
      const SearchResultPDF = ({ results }) => {
        return (
          <Document>
            <Page size="LETTER" style={styles.page}>
              {results.map((result, index) => (
                <View key={index} style={styles.resultContainer} wrap={false}>
                  {/* Title in its own container */}
                  <View style={styles.titleContainer}>
                    <Text style={styles.title}>{result.Name}</Text>
                  </View>
                  {/* Content and QR Code in a row */}
                  <View style={styles.contentRow}>
                    <View style={styles.content}>
                      <View style={styles.section}>
                       <Text style={styles.label}>{translatedLabels.services}: </Text>
                       <Text style={styles.text}>{result.Services}</Text>
                    
                       <Text style={styles.label}>{translatedLabels.eligibility}: </Text>
                       <Text style={styles.text}>{result.Eligibility_Requirement}</Text>
    
                       <Text style={styles.label}>{translatedLabels.phone}: </Text>
                       <Text style={styles.text}>{result.Phone}</Text>
    
                       <Text style={styles.label}>{translatedLabels.email}: </Text>
                       <Text style={styles.text}>{result.Email}</Text>
    
                       <Text style={styles.label}>{translatedLabels.address}: </Text>
                       {result.Address.map((line, i) => (
                        <Text key={i} style={styles.text}>{line}</Text>
                       ))}
    
    
                       <Text style={styles.label}>{translatedLabels.website}: </Text>
                       <Text style={styles.text}>{result.Website}</Text>
    
                       <Text style={styles.label}>{translatedLabels.howToApply}: </Text>
                       <Text style={styles.text}>{result.How_To_Apply}</Text>
                      </View>
                    </View>
                    {/* QR Code container */}
                    <View style={styles.qrCodeContainer}>
                      <QRCodeImage website={result.Website} />
                    </View>
                  </View>
                </View>
              ))}
            </Page>
          </Document>
        );
      };
      
        // Helper function to check if a field is valid (not "N/A", "NA", or empty string)
        const isValidField = (field) => {
        return typeof field === 'string' && field.toLowerCase() !== "n/a" && field.toLowerCase() !== "na" && field.trim() !== '';
        };
    
        // Function to find URLs in text and clean them
        const extractAndCleanURL = (text) => {
        if (!text) return null;
    
        // Enhanced regex to handle URLs with or without punctuation
        const urlPattern = /(?:\()?(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.(com|org|net|gov|edu|info))(?:\))?/gi;
    
        const match = text.match(urlPattern);
        if (match) {
            // Extract the first valid URL found and clean surrounding parentheses
            const cleanedMatch = match[0].replace(/[()]/g, "");
            return cleanedMatch.startsWith('http') ? cleanedMatch : `http://${cleanedMatch}`;
        }
        return null;
        };
    
        // This component generates the QR code for each result
        const QRCodeImage = ({ website }) => {
        const [qrCodeDataURL, setQrCodeDataURL] = useState('');
    
        useEffect(() => {
            // Validate and clean the URL before generating the QR code
            if (isValidField(website)) {
            const cleanedUrl = extractAndCleanURL(website);
            
            if (cleanedUrl) {
                QRCode.toDataURL(cleanedUrl)
                .then((url) => {
                    setQrCodeDataURL(url);
                })
                .catch((err) => {
                    console.error('Error generating QR code', err);
                });
            }
            }
        }, [website]);
    
        // Conditionally render the QR code or a message if there's no valid URL
        return (
            qrCodeDataURL ? (
            <View style={styles.qrCodeContainer}>
                <Image source={{ uri: qrCodeDataURL }} style={styles.qrCodeImage} />
            </View>
            ) : (
            <Text></Text>
            )
        );
        };
    
        try {
          // Generate the single-page PDF for all selected results
          const pdfBlob = await pdf(<SearchResultPDF results={translatedResults} />).toBlob();
          const url = URL.createObjectURL(pdfBlob);
          window.open(url, '_blank');
        } catch (error) {
          console.error('Error generating combined PDF:', error);
        }
      };

    return (
        <div>
            <div className="search-header">
                <div className="back-button-container">
                    <button className="back-button" onClick={() => navigate("/")}>
                        <FontAwesomeIcon icon={faHome} style={{ color: "white" }} />
                    </button>
                </div>
                <h1 style={{ textAlign: 'center' }}>Favorites</h1>
            </div>
            {loading ? (
                <p>Loading...</p> // Show loading message until data is fetched
            ) : (
                <>
                    {resources.length > 0 ? (
                        <div className="entries">
                            {resources.map((resource) => (
                                <div
                                    key={resource._id}
                                    style={{ width: "70%", marginLeft: "auto", marginRight: "auto" }}
                                    className="search-result"
                                >
                                    <Link to={`/detail/${resource._id}`} state={resource} className="search-result-link">
                                        <h2>{resource.Name}</h2>
                                    </Link>

                                    {/* Services */}
                                    {isValidField(resource.Services) && (
                                    <p><strong>Services: </strong> <span dangerouslySetInnerHTML={createMarkup(resource.Services)} /></p>
                                    )}

                                    {/* Eligibility Demographic */}
                                    {isValidField(resource.Eligibility_Demographic) && (
                                    <p><strong>Eligibility Demographic: </strong> {resource.Eligibility_Demographic}</p>
                                    )}

                                    {/* Phone */}
                                    {isValidField(resource.Phone) && (
                                    <p><strong>Phone: </strong> {resource.Phone}</p>
                                    )}

                                    {/* Address */}
                                    {resource.Address && (
                                        <p>
                                            <strong>Address: </strong>
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                    resource.Address
                                                )}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {resource.Address}
                                            </a>
                                        </p>
                                    )}


                                    {/* Website */}
                                    {isValidField(resource.Website) && (
                                    <p><strong>Website: </strong> <span dangerouslySetInnerHTML={createMarkup(resource.Website || 'N/A')} /></p>
                                    )}

                                    {/* How to Apply */}
                                    {isValidField(resource.How_To_Apply) && (
                                    <p><strong>How to Apply: </strong> <span dangerouslySetInnerHTML={createMarkup(resource.How_To_Apply)} /></p>
                                    )}

                                    {/* Unfavorite Button */}
                                    <button
                                        className="unfavorite-button"
                                        onClick={() => handleToggleFavorite(resource._id)}
                                    >
                                        <FontAwesomeIcon icon={solidStar} className="favorited-icon" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>No favorites yet.</p>
                    )}

                    <div className="pagination">
                        <button
                            className="navButtons"
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                            disabled={page === 1}
                        >
                            Previous
                        </button>
                        <span>
                            Page {page} of {totalPages}
                        </span>
                        <button
                            className="navButtons"
                            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={page === totalPages}
                        >
                            Next
                        </button>
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <button className="navButtons" onClick={generateCombinedPDF}>
                                Print Favorites
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Favorites;